import React from 'react';
import { ArrowRight, Plus, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getSpecialiteColor } from '@/types/calendar';
import { EquipeIcon } from '@/components/equipe/EquipeIcon';
import { ChantierIcon } from '@/components/chantiers/ChantierIcon';
import { Checkbox } from '@/components/ui/checkbox';
import type { MembreDisponibilite } from '@/hooks/useDisponibilites';

interface MembreRotationCardProps {
  membre: MembreDisponibilite;
  onRotation?: (membre: MembreDisponibilite) => void;
  onAffecter?: (membre: MembreDisponibilite) => void;
  // Props pour la multi-sélection
  showCheckbox?: boolean;
  isSelected?: boolean;
  onSelectChange?: (id: string, selected: boolean) => void;
}

/**
 * Carte d'un membre avec ses infos et bouton d'action
 * - Pour membres affectés : bouton "Rotation"
 * - Pour membres disponibles : bouton "Affecter"
 */
const MembreRotationCard = ({
  membre,
  onRotation,
  onAffecter,
  showCheckbox = false,
  isSelected = false,
  onSelectChange,
}: MembreRotationCardProps) => {
  const specialite = membre.specialite || membre.equipe_specialite;
  const colors = getSpecialiteColor(specialite);
  const isAffecte = membre.disponibilite === 'affecte';

  // Initiales du membre
  const initiales = `${membre.prenom?.[0] || ''}${membre.nom?.[0] || ''}`.toUpperCase();

  // Handler pour le clic sur la ligne (toggle si checkbox visible)
  const handleRowClick = () => {
    if (showCheckbox && onSelectChange) {
      onSelectChange(membre.id, !isSelected);
    }
  };

  return (
    <div
      className={cn(
        'flex items-center justify-between p-4',
        'bg-card/50 border border-border/50 rounded-xl',
        'hover:border-border hover:bg-card/80 transition-all duration-200',
        'group',
        showCheckbox && 'cursor-pointer',
        showCheckbox && !isSelected && 'opacity-60'
      )}
      onClick={handleRowClick}
    >
      {/* Infos membre */}
      <div className="flex items-center gap-3 min-w-0">
        {/* Checkbox (si activée) */}
        {showCheckbox && (
          <Checkbox
            checked={isSelected}
            onCheckedChange={(checked) => onSelectChange?.(membre.id, !!checked)}
            onClick={(e) => e.stopPropagation()}
            className="flex-shrink-0"
          />
        )}
        {/* Avatar avec initiales */}
        <div
          className={cn(
            'w-10 h-10 rounded-full flex items-center justify-center',
            'text-white font-bold text-sm flex-shrink-0',
            colors.dot
          )}
        >
          {initiales || <User className="w-5 h-5" />}
        </div>

        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-medium text-foreground truncate">
              {membre.prenom} {membre.nom}
            </p>
            {/* Pictogramme équipe */}
            {membre.equipe_id && (
              <EquipeIcon
                pictogramme={membre.equipe_pictogramme || 'Users'}
                couleur={membre.equipe_couleur || '#f97316'}
                size="sm"
              />
            )}
          </div>
          <div className="flex items-center gap-2 text-sm text-foreground/80 font-medium">
            {membre.poste && <span>{membre.poste}</span>}
            {specialite && (
              <span className={cn('px-1.5 py-0.5 rounded text-xs font-semibold', colors.bg, colors.text)}>
                {specialite}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Infos droite */}
      <div className="flex items-center gap-4">
        {isAffecte ? (
          // Membre affecté : afficher chantier + bouton rotation
          <>
            <div className="text-right hidden sm:block">
              <div className="flex items-center justify-end gap-1.5 text-sm text-foreground">
                <ChantierIcon
                  pictogramme={membre.chantier_pictogramme || 'Building2'}
                  couleur={membre.chantier_couleur || '#3b82f6'}
                  size="sm"
                />
                <span className="truncate max-w-[120px]">
                  {membre.reference_chantier || membre.chantier_actuel || 'Chantier'}
                </span>
                {/* Badge Retard si affectation de retard */}
                {membre.is_retard && (
                  <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-orange-500/20 text-orange-500 rounded">
                    Retard
                  </span>
                )}
              </div>
            </div>

            {/* Bouton rotation chantier */}
            {onRotation && (
              <button
                onClick={() => onRotation(membre)}
                className={cn(
                  'px-3 py-1.5 rounded-lg transition-all duration-200',
                  'bg-orange-500/10 text-orange-500 text-xs font-medium',
                  'hover:bg-orange-500/20',
                  'opacity-80 group-hover:opacity-100',
                  'flex items-center gap-1.5'
                )}
                title="Rotation vers un autre chantier"
              >
                <ArrowRight className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Rotation</span>
              </button>
            )}
          </>
        ) : (
          // Membre disponible : afficher badge + bouton affecter
          <>
            <span className="px-3 py-1 text-xs bg-green-500/20 text-green-400 rounded-full font-semibold">
              Disponible
            </span>

            {/* Bouton affecter à chantier */}
            {onAffecter && (
              <button
                onClick={() => onAffecter(membre)}
                className={cn(
                  'px-3 py-1.5 rounded-lg transition-all duration-200',
                  'bg-amber-500/10 text-amber-500 text-xs font-semibold',
                  'hover:bg-amber-500/20',
                  'opacity-80 group-hover:opacity-100',
                  'flex items-center gap-1.5'
                )}
                title="Affecter à un chantier"
              >
                <Plus className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Affecter</span>
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MembreRotationCard;
